import axios from 'axios';

// ─────────────────────────────────────────
// Image Base URL (use this in components)
// ─────────────────────────────────────────
export var IMAGE_BASE_URL =
  process.env.REACT_APP_IMAGE_URL || 'https://study.richmindx.in';

// ─────────────────────────────────────────
// Axios Instance
// ─────────────────────────────────────────
var API = axios.create({
  baseURL: process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || 'https://study.richmindx.in/api',
  // DO NOT set Content-Type here — axios sets it automatically:
  // - 'application/json'       for plain objects
  // - 'multipart/form-data'    for FormData (with correct boundary)
  // If you force 'application/json' globally, multer cannot parse
  // FormData and throws: "Cannot destructure property 'name' of req.body"
});

// ─────────────────────────────────────────────────────────────────────────────
// Admin Profile request de-dupe + short cache (prevents double calls in dev)
// ─────────────────────────────────────────────────────────────────────────────
var ADMIN_PROFILE_CACHE_MS = 4000;
var adminProfileCache = null;
var adminProfileCacheTime = 0;
var adminProfileInFlight = null;

var setAdminProfileCache = function (res) {
  adminProfileCache = res;
  adminProfileCacheTime = Date.now();
};

// Attach admin token to every request
API.interceptors.request.use(function (req) {
  var token =
    localStorage.getItem('adminToken') ||
    sessionStorage.getItem('adminToken') ||
    localStorage.getItem('token') ||
    sessionStorage.getItem('token');
  if (token) {
    req.headers['Authorization'] = 'Bearer ' + token;
    req.headers['x-auth-token'] = token;
    req.headers['x-access-token'] = token;
    req.headers['token'] = token;
  }

  var isFormData = function (value) {
    return value && typeof value === 'object' && typeof value.append === 'function';
  };

  // KEY FIX: If the request body is FormData, DELETE any Content-Type
  // that may have been set, so the browser sets it automatically with
  // the correct multipart boundary. Without this, multer fails.
  if (isFormData(req.data)) {
    if (req.headers) {
      if (req.headers['Content-Type']) delete req.headers['Content-Type'];
      if (req.headers.common && req.headers.common['Content-Type']) {
        delete req.headers.common['Content-Type'];
      }
      // Also clear from the method-specific headers if set
      if (req.headers.post && req.headers.post['Content-Type']) delete req.headers.post['Content-Type'];
      if (req.headers.put && req.headers.put['Content-Type']) delete req.headers.put['Content-Type'];
    }
  }

  return req;
});

// Global response error handler
API.interceptors.response.use(
  function (response) { return response; },
  function (error) {
    if (error.response && error.response.status === 401) {
      try {
        localStorage.removeItem('adminToken');
        sessionStorage.removeItem('adminToken');
        localStorage.removeItem('token');
        sessionStorage.removeItem('token');
      } catch (e) {}
    }
    return Promise.reject(error);
  }
);

// Retry update calls against common REST variants so edit screens keep working
var putOrPatch = function (path, data) {
  return API.put(path, data).catch(function (error) {
    if (error && error.response && error.response.status === 405) {
      return API.patch(path, data);
    }
    return Promise.reject(error);
  });
};

var tryRequestFallbacks = function (calls) {
  var run = function (index) {
    if (index >= calls.length) {
      return Promise.reject(new Error('No API fallback matched.'));
    }
    return calls[index]().catch(function (error) {
      if (index >= calls.length - 1) return Promise.reject(error);
      return run(index + 1);
    });
  };
  return run(0);
};

// ─────────────────────────────────────────
// AUTH
// ─────────────────────────────────────────
export var adminLogin  = function (data) { return API.post('/admin/login', data); };
export var adminForgotPassword = function (data) { return API.post('/admin/forgot-password', data); };
export var adminResetPassword = function (data) { return API.post('/admin/reset-password', data); };
export var createAdmin = function (data) { return API.post('/admin/create', data); };

// ADMIN ROLES & PERMISSIONS
export var getAllRoles             = function ()           { return API.get('/admin/roles'); };
export var getRoleById            = function (id)         { return API.get('/admin/roles/' + id); };
export var createRole             = function (data)       { return API.post('/admin/roles', data); };
export var updateRole             = function (id, data)   { return API.put('/admin/roles/' + id, data); };
export var deleteRole             = function (id)         { return API.delete('/admin/roles/' + id); };
export var assignRolePermissions  = function (id, data)   { return API.post('/admin/roles/' + id + '/permissions', data); };
export var assignRoleToAdmin      = function (adminId, roleId) { return API.put('/admin/' + adminId + '/role', { roleId: roleId }); };
export var getMyPermissions       = function ()           { return API.get('/admin/me/permissions'); };
export var getPermissionModules   = function ()           { return API.get('/admin/permissions/modules'); };

// ADMIN EMPLOYEES
export var getEmployees = function (params) { return API.get('/admin/employees', { params: params || {} }); };
export var getEmployeeById = function (id) { return API.get('/admin/employees/' + id); };
export var createEmployee = function (data) { return API.post('/admin/employees', data); };
export var updateEmployee = function (id, data) { return putOrPatch('/admin/employees/' + id, data); };
export var deleteEmployee = function (id) { return API.delete('/admin/employees/' + id); };
export var employeeLogin = function (data) { return API.post('/employee/login', data); };

// ADMIN PROFILE
export var getAdminProfile = function () {
  var now = Date.now();
  if (adminProfileCache && now - adminProfileCacheTime < ADMIN_PROFILE_CACHE_MS) {
    return Promise.resolve(adminProfileCache);
  }
  if (adminProfileInFlight) return adminProfileInFlight;

  adminProfileInFlight = API.get('/admin/profile')
    .then(function (res) {
      setAdminProfileCache(res);
      adminProfileInFlight = null;
      return res;
    })
    .catch(function (err) {
      adminProfileInFlight = null;
      throw err;
    });

  return adminProfileInFlight;
};

export var updateAdminProfile = function (data) {
  return API.put('/admin/profile', data)
    .then(function (res) {
      setAdminProfileCache(res);
      return res;
    });
};

export var getAdminRevenue  = function ()          { return API.get('/admin/revenue'); };
export var getDashboardSummary = function (from, to) {
  var params = [];
  if (from) params.push('from=' + encodeURIComponent(from));
  if (to)   params.push('to='   + encodeURIComponent(to));
  var query = params.length ? '?' + params.join('&') : '';
  return API.get('/admin/dashboard/summary' + query);
};

// ─────────────────────────────────────────
// CATEGORY
// FormData is passed directly — axios + interceptor handles Content-Type
// ─────────────────────────────────────────
export var getAllCategories = function ()          { return API.get('/category?all=true'); };
export var getCategoryById  = function (id)       { return API.get('/category/' + id); };
export var createCategory   = function (data)     { return API.post('/category', data); };
export var updateCategory   = function (id, data) { return putOrPatch('/category/' + id, data); };
export var deleteCategory   = function (id)       { return API.delete('/category/' + id); };

// ─────────────────────────────────────────
// SUBCATEGORY
// ─────────────────────────────────────────
// ?all=true so the admin panel can see inactive subcategories too, not
// just the "active" ones the public site would show (matches getAllCategories).
export var getAllSubcategories         = function ()           { return API.get('/subcategory?all=true'); };
export var getSubcategoryById         = function (id)         { return API.get('/subcategory/' + id); };
export var getSubcategoriesByCategory = function (categoryId) { return API.get('/subcategory/by-category/' + categoryId); };
export var createSubcategory          = function (data)       { return API.post('/subcategory', data); };
export var updateSubcategory          = function (id, data)   { return putOrPatch('/subcategory/' + id, data); };
export var deleteSubcategory          = function (id, config) {
  return config
    ? API.delete('/subcategory/' + id, config)
    : API.delete('/subcategory/' + id);
};

// ─────────────────────────────────────────
// PRODUCT
// ─────────────────────────────────────────
// Admin table now paginates server-side (see Products.js) — only the
// current page is requested, not the whole catalog.
export var getAllProducts = function (page, limit) {
  return API.get('/product', { params: { page: page || 1, limit: limit || 20 } });
};
// For the few places that legitimately need the full list: the Top Picks
// product picker, the admin global search bar, and Dashboard aggregates.
export var getAllProductsUnpaginated = function () {
  return API.get('/product', { params: { all: 'true' } });
};
export var getProductById = function (id)       { return API.get('/product/' + id); };
export var createProduct  = function (data)     { return API.post('/product', data); };
export var updateProduct  = function (id, data) {
  return putOrPatch('/product/' + id, data).catch(function (error) {
    var status = error && error.response && error.response.status;
    if (status === 404 || status === 405) {
      return putOrPatch('/product/update/' + id, data).catch(function (error2) {
        var status2 = error2 && error2.response && error2.response.status;
        if (status2 === 404 || status2 === 405) {
          return putOrPatch('/product/' + id + '/flags', data);
        }
        return Promise.reject(error2);
      });
    }
    return Promise.reject(error);
  });
};
export var deleteProduct  = function (id)       { return API.delete('/product/' + id); };
export var toggleProductStatus = function (id) { return API.patch('/product/' + id + '/status'); };

// ─────────────────────────────────────────
// ORDERS
// ─────────────────────────────────────────
export var createOrder        = function (data) { return API.post('/order', data); };
export var postOrder          = function (data) { return createOrder(data); };
// Admin Orders/Payments pages paginate client-side, so fetch everything (all=true).
export var getAllOrders       = function (from, to) {
  var params = ['all=true'];
  if (from) params.push('from=' + encodeURIComponent(from));
  if (to)   params.push('to='   + encodeURIComponent(to));
  var query = '?' + params.join('&');
  return API.get('/order' + query);
};
export var getOrderById       = function (id)       { return API.get('/order/' + id); };
export var updateOrderStatus  = function (id, data) { return putOrPatch('/order/' + id, data); };
export var updateOrderShippingAddress = function (id, data) {
  var payload = Object.assign({}, data, {
    shippingAddress: {
      name: data.name,
      phone: data.phone,
      address: data.address,
      address2: data.address2,
      city: data.city,
      state: data.state,
      pincode: data.pincode,
      country: data.country,
    },
    address: data.address,
    address2: data.address2,
    city: data.city,
    state: data.state,
    pincode: data.pincode,
    country: data.country,
    customerName: data.name,
    customerPhone: data.phone,
  });

  return putOrPatch('/order/' + id, payload);
};
export var deleteOrder        = function (id)       { return API.delete('/order/' + id); };

// ─────────────────────────────────────────
// SHIPROCKET
// ─────────────────────────────────────────
export var getShiprocketOrders = function () { return API.get('/shiprocket/admin/orders'); };
export var createShipment      = function (orderId) { return API.post('/shiprocket/admin/orders/' + orderId + '/create-shipment', {}); };
export var assignAWB           = function (orderId, courierId) {
  return API.post('/shiprocket/admin/orders/' + orderId + '/assign-awb', courierId ? { courierId } : {});
};
export var getCourierOptions   = function (orderId) {
  return API.get('/shiprocket/admin/orders/' + orderId + '/courier-options');
};
export var getShippingLabel    = function (orderId) { return API.get('/shiprocket/admin/orders/' + orderId + '/label'); };
export var getShippingInvoice  = function (orderId) { return API.get('/shiprocket/admin/orders/' + orderId + '/invoice'); };

// ─────────────────────────────────────────
// CUSTOMERS
// ─────────────────────────────────────────
export var createCustomer      = function (data) { return API.post('/customer', data); };
export var postCustomer        = function (data) { return createCustomer(data); };
// Admin Customers page paginates client-side, so fetch everything (all=true).
export var getAllCustomers     = function ()                     { return API.get('/customer', { params: { all: 'true' } }); };
export var getCustomerById     = function (id)                   { return API.get('/customer/' + id); };
export var updateCustomer      = function (id, data)             { return putOrPatch('/customer/' + id, data); };
export var deleteCustomer      = function (id)                   { return API.delete('/customer/' + id); };
export var blockCustomer       = function (id, reason)            { return API.put('/customer/' + id + '/block', { reason }); };
export var unblockCustomer     = function (id)                    { return API.put('/customer/' + id + '/unblock'); };
export var getCustomersPaginated = function (page, limit) {
  return API.get('/customer?page=' + (page || 1) + '&limit=' + (limit || 10));
};

// ─────────────────────────────────────────
// ENQUIRIES
// ─────────────────────────────────────────
export var getAllEnquiries      = function ()          { return API.get('/enquiry'); };
export var getEnquiryById       = function (id)       { return API.get('/enquiry/' + id); };
export var createEnquiry        = function (data)     { return API.post('/enquiry', data); };
export var updateEnquiryStatus  = function (id, data) { return putOrPatch('/enquiry/' + id, data); };
export var deleteEnquiry        = function (id)       { return API.delete('/enquiry/' + id); };


// ─────────────────────────────────────────
// SITE SETTINGS
// ─────────────────────────────────────────
export var getSiteSettings    = function ()      { return API.get('/settings'); };
export var updateSiteSettings = function (data)  { return putOrPatch('/settings', data); };
export var getGstSettings     = function ()      { return API.get('/settings/gst'); };
export var updateGstSettings  = function (data)  { return API.put('/settings/gst', data); };
export var getFreeShippingSettings = function () { return API.get('/settings/free-shipping'); };
export var updateFreeShippingSettings = function (data) { return API.put('/settings/free-shipping', data); };


// ─────────────────────────────────────────
// PAYMENTS
// ─────────────────────────────────────────
export var payWithCOD    = function (data) { return API.post('/payment/cod', data); };
export var payWithOnline = function (data) { return API.post('/payment/online', data); };
export var getAllPayments = function (params) {
  return tryRequestFallbacks([
    function () { return API.get('/payments', { params: params || {} }); },
    function () { return API.get('/payment', { params: params || {} }); },
  ]);
};

// ─────────────────────────────────────────
// SUPER COINS
// ─────────────────────────────────────────
export var getSuperCoinsDashboard = function () {
  return tryRequestFallbacks([
    function () { return API.get('/admin/super-coins/dashboard'); },
    function () { return API.get('/admin/super-coins/summary'); },
  ]);
};
export var getSuperCoinsSummary = function () {
  return tryRequestFallbacks([
    function () { return API.get('/admin/super-coins/summary'); },
    function () { return API.get('/admin/super-coins/dashboard'); },
  ]);
};
export var getSuperCoinRules = function () { return API.get('/admin/super-coins/rules'); };
export var createSuperCoinRule = function (data) { return API.post('/admin/super-coins/rules', data); };
export var updateSuperCoinRule = function (id, data) { return putOrPatch('/admin/super-coins/rules/' + id, data); };
export var deleteSuperCoinRule = function (id) { return API.delete('/admin/super-coins/rules/' + id); };
export var getActiveSuperCoinRules = function () { return API.get('/super-coins/rules/active'); };
export var getSuperCoinSettings = function () {
  return tryRequestFallbacks([
    function () { return API.get('/admin/super-coins/settings'); },
    function () { return API.get('/super-coins/rules/active'); }
  ]);
};
export var updateSuperCoinSettings = function (data) {
  return API.put('/admin/super-coins/settings', data);
};
export var getCustomerSuperCoins = function (params) {
  return API.get('/admin/super-coins/customers', { params: params || {} });
};
export var getCustomerSuperCoinDetails = function (customerId) {
  return tryRequestFallbacks([
    function () { return API.get('/admin/super-coins/customers/' + customerId); },
    function () { return API.get('/admin/super-coins/customers/' + customerId + '/history'); }
  ]);
};
export var getCustomerSuperCoinHistory = function (customerId, pageOrParams, limit) {
  var params = (typeof pageOrParams === 'object' && pageOrParams !== null)
    ? pageOrParams
    : { page: pageOrParams || 1, limit: limit || 10 };
  return API.get('/admin/super-coins/customers/' + customerId + '/history', {
    params: params
  });
};
export var addCustomerSuperCoins = function (data) {
  var customerId = data && data.customerId ? data.customerId : data && data.customer_id ? data.customer_id : data && data.id;
  return API.post('/admin/super-coins/adjust', {
    customerId: customerId,
    coins: Number(data && data.coins),
    type: (data && data.type) || 'credit',
    reason: (data && (data.reason || data.remarks || data.note)) || 'Manual credit'
  });
};
export var adjustCustomerSuperCoins = function (data) {
  return API.post('/admin/super-coins/adjust', data);
};
export var getSuperCoinTransactions = function (params) {
  return API.get('/admin/super-coins/transactions', { params: params || {} });
};
export var refundSuperCoins = function (orderIdOrData) {
  var orderId = (typeof orderIdOrData === 'object' && orderIdOrData !== null)
    ? orderIdOrData.orderId
    : orderIdOrData;
  return API.post('/admin/super-coins/refund', { orderId: orderId });
};

// ─────────────────────────────────────────
// ANNOUNCEMENTS / MARQUEE
// ─────────────────────────────────────────
export var getAllAnnouncements = function () {
  return tryRequestFallbacks([
    function () { return API.get('/announcements'); },
    function () { return API.get('/announcement'); },
    function () { return API.get('/admin/announcements'); },
    function () { return API.get('/admin/announcement'); }
  ]);
};
export var createAnnouncement = function (data) {
  return tryRequestFallbacks([
    function () { return API.post('/announcements', data); },
    function () { return API.post('/announcement', data); },
    function () { return API.post('/admin/announcements', data); },
    function () { return API.post('/admin/announcement', data); }
  ]);
};
export var updateAnnouncement = function (id, data) {
  return tryRequestFallbacks([
    function () { return putOrPatch('/announcements/' + id, data); },
    function () { return putOrPatch('/announcement/' + id, data); },
    function () { return putOrPatch('/admin/announcements/' + id, data); },
    function () { return putOrPatch('/admin/announcement/' + id, data); }
  ]);
};
export var toggleAnnouncementStatus = function (id, status) {
  return tryRequestFallbacks([
    function () { return API.patch('/announcements/' + id + '/status', { status: status }); },
    function () { return API.patch('/announcement/' + id + '/status', { status: status }); },
    function () { return API.patch('/admin/announcements/' + id + '/status', { status: status }); },
    function () { return API.patch('/admin/announcement/' + id + '/status', { status: status }); },
    function () { return API.put('/announcements/' + id + '/status', { status: status }); }
  ]);
};
export var deleteAnnouncement = function (id) {
  return tryRequestFallbacks([
    function () { return API.delete('/announcements/' + id); },
    function () { return API.delete('/announcement/' + id); },
    function () { return API.delete('/admin/announcements/' + id); },
    function () { return API.delete('/admin/announcement/' + id); }
  ]);
};

// ─────────────────────────────────────────
// NOTIFICATIONS
// ─────────────────────────────────────────
export var getAllNotifications         = function ()        { return API.get('/notifications'); };
export var getNotificationById        = function (id)      { return API.get('/notifications/' + id); };
export var createNotification         = function (data)    { return API.post('/notifications', data); };
export var getNotificationsByStatus   = function (status)  { return API.get('/notifications/status/' + status); };
export var markNotificationAsRead     = function (id)      { return API.put('/notifications/' + id + '/read'); };
export var deleteNotification         = function (id)      { return API.delete('/notifications/' + id); };

// ─────────────────────────────────────────
// OFFERS
// ─────────────────────────────────────────
export var getAllOffers  = function (params)    { return API.get('/offer', { params: params || {} }); };
export var getOfferById = function (id)        { return API.get('/offer/' + id); };
export var createOffer  = function (data)      { return API.post('/offer', data); };
export var updateOffer  = function (id, data)  { return putOrPatch('/offer/' + id, data); };
export var deleteOffer  = function (id)        { return API.delete('/offer/' + id); };

// ─────────────────────────────────────────
// BANNERS
// ─────────────────────────────────────────
export var getAllBanners    = function ()          { return API.get('/banners'); };
export var getActiveBanners = function ()          { return API.get('/banners/active'); }; // customer-facing hero
export var getBannerById    = function (id)       { return API.get('/banners/' + id); };
export var createBanner     = function (data)     { return API.post('/banners/create', data); };
export var updateBanner     = function (id, data) { return putOrPatch('/banners/' + id, data); };
export var deleteBanner     = function (id)       { return API.delete('/banners/' + id); };

// ─────────────────────────────────────────
// TOP PICKS
// ─────────────────────────────────────────
export var getAllTopPicks    = function ()          { return API.get('/toppicks/all'); };
export var getActiveTopPicks = function ()         { return API.get('/toppicks'); };
export var addTopPick        = function (data)     { return API.post('/toppicks', data); };
export var updateTopPick     = function (id, data) { return putOrPatch('/toppicks/' + id, data); };
export var deleteTopPick     = function (id)       { return API.delete('/toppicks/' + id); };

// Contact Enquiries
export var getAllContacts      = function ()          { return API.get('/admin/contacts'); };
export var updateContactStatus = function (id, data)  { return putOrPatch('/admin/contacts/' + id, data); };
export var deleteContact       = function (id)        { return API.delete('/admin/contacts/' + id); };

// Gift Packing
export var getGiftPackingStatistics = function () {
  return tryRequestFallbacks([
    function () { return API.get('/gift-packing/statistics'); },
    function () { return API.get('/giftpacking/statistics'); },
    function () { return API.get('/gift-packing/summary'); },
    function () { return API.get('/giftpacking/summary'); },
    function () { return API.get('/admin/gift-packing/statistics'); }
  ]);
};
export var getGiftPackingSummary = function () {
  return tryRequestFallbacks([
    function () { return API.get('/gift-packing/summary'); },
    function () { return API.get('/giftpacking/summary'); },
    function () { return API.get('/admin/gift-packing/summary'); },
    function () { return API.get('/admin/giftpacking/summary'); }
  ]);
};
export var getGiftPackingList = function (params) {
  return tryRequestFallbacks([
    function () { return API.get('/gift-packing', { params: params || {} }); },
    function () { return API.get('/giftpacking', { params: params || {} }); },
    function () { return API.get('/admin/gift-packing', { params: params || {} }); },
    function () { return API.get('/admin/giftpacking', { params: params || {} }); }
  ]);
};
export var getGiftPackingDetails = function (id) {
  return tryRequestFallbacks([
    function () { return API.get('/gift-packing/' + id); },
    function () { return API.get('/giftpacking/' + id); },
    function () { return API.get('/admin/gift-packing/' + id); },
    function () { return API.get('/admin/giftpacking/' + id); }
  ]);
};
export var createGiftPacking = function (data) {
  return tryRequestFallbacks([
    function () { return API.post('/gift-packing', data); },
    function () { return API.post('/giftpacking', data); },
    function () { return API.post('/admin/gift-packing', data); },
    function () { return API.post('/admin/giftpacking', data); }
  ]);
};
export var updateGiftPacking = function (id, data) {
  return tryRequestFallbacks([
    function () { return putOrPatch('/gift-packing/' + id, data); },
    function () { return putOrPatch('/giftpacking/' + id, data); },
    function () { return putOrPatch('/admin/gift-packing/' + id, data); },
    function () { return putOrPatch('/admin/giftpacking/' + id, data); }
  ]);
};
export var toggleGiftPackingStatus = function (id, status) {
  return tryRequestFallbacks([
    function () { return API.patch('/gift-packing/' + id + '/status', { status: status }); },
    function () { return API.patch('/giftpacking/' + id + '/status', { status: status }); },
    function () { return API.put('/gift-packing/' + id + '/status', { status: status }); },
    function () { return API.put('/giftpacking/' + id + '/status', { status: status }); }
  ]);
};
export var deleteGiftPacking = function (id) {
  return tryRequestFallbacks([
    function () { return API.delete('/gift-packing/' + id); },
    function () { return API.delete('/giftpacking/' + id); },
    function () { return API.delete('/admin/gift-packing/' + id); },
    function () { return API.delete('/admin/giftpacking/' + id); }
  ]);
};

// Reports
export var getReportsOverview     = function (params) { return API.get('/admin/reports', { params: params }); };
export var getCustomerOrderHistory = function (customerId) { return API.get('/admin/reports/customers/' + customerId + '/orders'); };
export var exportReportFile       = function (section, params, format) { return API.get('/admin/reports/export', { params: Object.assign({ section: section, format: format }, params) }); };

export default API;

// import axios from 'axios';

// // ─────────────────────────────────────────
// // Image Base URL (use this in components)
// // ─────────────────────────────────────────
// export const IMAGE_BASE_URL =
//   process.env.REACT_APP_IMAGE_URL || 'https://study.richmindx.in';

// // ─────────────────────────────────────────
// // Axios Instance
// // ─────────────────────────────────────────
// const API = axios.create({
//   baseURL: process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || 'https://study.richmindx.in/api',
// });

// // Attach admin token to every request
// API.interceptors.request.use((req) => {
//   const token = localStorage.getItem('adminToken');
//   if (token) {
//     req.headers.Authorization = `Bearer ${token}`;
//   }
//   return req;
// });

// // Global response error handler
// API.interceptors.response.use(
//   (response) => response,
//   (error) => {
//     if (error.response?.status === 401) {
//       localStorage.removeItem('adminToken');
//       window.location.href = '/admin/login';
//     }
//     return Promise.reject(error);
//   }
// );

// // ─────────────────────────────────────────
// // AUTH
// // ─────────────────────────────────────────
// export const adminLogin  = (data) => API.post('/admin/login', data);
// export const createAdmin = (data) => API.post('/admin/create', data);

// // ─────────────────────────────────────────
// // CATEGORY
// // ─────────────────────────────────────────
// export const getAllCategories = ()           => API.get('/category');
// export const getCategoryById  = (id)        => API.get(`/category/${id}`);
// export const createCategory   = (data)      => API.post('/category', data);
// export const updateCategory   = (id, data)  => API.put(`/category/${id}`, data);
// export const deleteCategory   = (id)        => API.delete(`/category/${id}`);

// // ─────────────────────────────────────────
// // SUBCATEGORY
// // ─────────────────────────────────────────
// export const getAllSubcategories         = ()           => API.get('/subcategory');
// export const getSubcategoryById         = (id)         => API.get(`/subcategory/${id}`);
// export const getSubcategoriesByCategory = (categoryId) => API.get(`/subcategory/by-category/${categoryId}`);
// export const createSubcategory          = (data)       => API.post('/subcategory', data);
// export const updateSubcategory          = (id, data)   => API.put(`/subcategory/${id}`, data);
// export const deleteSubcategory          = (id)         => API.delete(`/subcategory/${id}`);

// // ─────────────────────────────────────────
// // PRODUCT
// // ─────────────────────────────────────────
// export const getAllProducts = ()           => API.get('/product');
// export const getProductById = (id)        => API.get(`/product/${id}`);
// export const createProduct  = (data)      => API.post('/product', data);
// export const updateProduct  = (id, data)  => API.put(`/product/${id}`, data);
// export const deleteProduct  = (id)        => API.delete(`/product/${id}`);

// // ─────────────────────────────────────────
// // ORDERS
// // ─────────────────────────────────────────
// export const getAllOrders      = ()           => API.get('/order');
// export const getOrderById      = (id)         => API.get(`/order/${id}`);
// export const updateOrderStatus = (id, data)   => API.put(`/order/${id}`, data);
// export const deleteOrder       = (id)         => API.delete(`/order/${id}`);

// // ─────────────────────────────────────────
// // CUSTOMERS
// // ─────────────────────────────────────────
// export const getAllCustomers        = ()                      => API.get('/customer');
// export const getCustomerById       = (id)                    => API.get(`/customer/${id}`);
// export const updateCustomer        = (id, data)              => API.put(`/customer/${id}`, data);
// export const deleteCustomer        = (id)                    => API.delete(`/customer/${id}`);
// export const getCustomersPaginated = (page = 1, limit = 10) => API.get(`/customer?page=${page}&limit=${limit}`);

// // ─────────────────────────────────────────
// // PAYMENTS
// // ─────────────────────────────────────────
// export const payWithCOD    = (data) => API.post('/payment/cod', data);
// export const payWithOnline = (data) => API.post('/payment/online', data);
// export const getAllPayments = ()     => API.get('/payment');

// export default API;
